import React from 'react';
import { useNavigate } from 'react-router-dom';
import GameViewportModal from '../GameViewportModal/GameViewportModal';
import { useAuth } from '../../hooks/useAuth';
import './EquipmentViewportModal.css';

/* Mirror of backend services/keystone_engine/registry.py::_ACCENT_RGB.
   Updates here MUST track the backend registry. */
const KEYSTONE_ACCENT_RGB = {
  vancomycin: '239, 68, 68',
  dupilumab: '96, 165, 250',
  metformin: '45, 212, 160',
};

/**
 * EquipmentViewportModal — composition wrapper around GameViewportModal
 * for player-loadout surfaces (Wholesaler / Pharmacy / Stats).
 *
 * Adds the keystone-accent palette + named eyebrow/title header strip
 * that the loadout surfaces consume; everything else (portal target
 * hunt, scroll lock, Escape, scrim, focus, animation) comes from the
 * underlying GameViewportModal primitive. This used to be a parallel
 * shell with ~80% overlap; rebuilt 2026-05-11 to delegate so we only
 * maintain the modal substrate in one place.
 *
 * Trigger pattern unchanged:
 *   navigate('/player/wholesaler', { state: { backgroundLocation: location, viewportOrb: true } })
 *
 * The .evm-* class names below are preserved for backward compatibility
 * with the per-surface CSS (PlayerShop.css, PlayerEquipment.css) that
 * scopes inside `.evm-body` to strip page chrome.
 */
export default function EquipmentViewportModal({
  children,
  onClose,
  eyebrow = 'Equipment',
  eyebrowMode,
  title = 'Pharmacy Loadout',
  subtitle,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const starterDrug = (user?.settings?.starter_drug || '').toLowerCase();
  const accentRgb = KEYSTONE_ACCENT_RGB[starterDrug];

  const handleClose = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  const accentStyle = accentRgb ? { '--evm-accent-rgb': accentRgb } : undefined;

  const header = (
    <header className="evm-header">
      <div className="evm-header-meta">
        <span className="evm-status-dot" />
        <span className={`evm-eyebrow${eyebrowMode ? ` evm-eyebrow--${eyebrowMode}` : ''}`}>{eyebrow}</span>
        {title && (
          <>
            <span className="evm-divider" aria-hidden="true">/</span>
            <span className="evm-title">{title}</span>
          </>
        )}
      </div>
      <div className="evm-header-actions">
        {subtitle && <span className="evm-subtitle">{subtitle}</span>}
      </div>
    </header>
  );

  return (
    <GameViewportModal
      open
      onClose={handleClose}
      ariaLabel={title || eyebrow}
      variantClassName="equipment"
      header={header}
      bodyStyle={accentStyle}
    >
      <div className="evm-body">
        {children}
      </div>
    </GameViewportModal>
  );
}
